import { useState } from 'react';
import { X, TriangleAlert, CircleAlert, IdCard, CreditCard } from '@/components/icons';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Separator } from '@/components/ui/separator';
import {
  useDocumentGetExpired,
  useDocumentGetExpiring,
} from '@/lib/hooks';
import { ViewDocumentDrawer } from '@/components/documents/ViewDocumentDrawer';
import { EmptyState } from '@/components/EmptyState';
import dayjs from 'dayjs';

interface OperationsReviewDocumentsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided, only documents with these IDs are listed. */
  documentIds?: string[];
}

export function OperationsReviewDocumentsDrawer({
  open,
  onOpenChange,
  documentIds,
}: OperationsReviewDocumentsDrawerProps) {
  const { data: expiredCrewDocs = [] } = useDocumentGetExpired({ category: 'Crew' });
  const { data: expiringCrewDocs = [] } = useDocumentGetExpiring({ category: 'Crew', daysAhead: 30 });
  const { data: expiredComplianceDocs = [] } = useDocumentGetExpired({ category: 'Compliance' });
  const { data: expiringComplianceDocs = [] } = useDocumentGetExpiring({ category: 'Compliance', daysAhead: 30 });

  const [selectedCrewDocId, setSelectedCrewDocId] = useState<string | null>(null);
  const [selectedComplianceDocId, setSelectedComplianceDocId] = useState<string | null>(null);
  // Separate open flags; the ids are kept on close so the view drawers can
  // play their exit animation (the next tap overwrites them).
  const [isCrewDocOpen, setIsCrewDocOpen] = useState(false);
  const [isComplianceDocOpen, setIsComplianceDocOpen] = useState(false);

  const isExpired = (date: Date | string) => dayjs(date).isBefore(dayjs(), 'day');

  const idFilter = documentIds ? new Set(documentIds) : null;
  const allCrewDocs = [...expiredCrewDocs, ...expiringCrewDocs].filter(
    (doc: any) => !idFilter || idFilter.has(doc.id),
  );
  const allComplianceDocs = [...expiredComplianceDocs, ...expiringComplianceDocs].filter(
    (doc: any) => !idFilter || idFilter.has(doc.id),
  );

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="bg-surface-L0 max-h-[85dvh] flex flex-col">
          <DrawerHeader className="flex items-center justify-between p-4">
            <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
              Review Documents
            </DrawerTitle>
            <button
              onClick={() => onOpenChange(false)}
              className="p-1 hover:bg-surface-L2 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-content-primary-subtle" />
            </button>
          </DrawerHeader>

          <div className="px-4 pb-4">
            <div className="flex flex-col gap-3">
              {/* Crew Documents */}
              {allCrewDocs.map((doc: any) => {
                const expired = isExpired(doc.expiryDate);
                const crewName = doc.crew?.name || `${doc.crew?.firstName ?? ''} ${doc.crew?.lastName ?? ''}`.trim();

                return (
                  <button
                    key={doc.id}
                    onClick={() => {
                      setSelectedCrewDocId(doc.id);
                      setIsCrewDocOpen(true);
                    }}
                    className="w-full text-left border border-border-L2 rounded-2xl px-4 py-2 flex flex-col gap-1 hover:bg-surface-L2 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-base font-semibold text-content-primary-bold">{crewName}</p>
                      {expired ? (
                        <span className="flex items-center gap-1 text-xs font-medium text-content-negative-bold bg-background-negative-subtle px-2 py-0.5 rounded-full">
                          <TriangleAlert className="size-4" />
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs font-medium text-background-notice-bold bg-background-notice-subtle px-2 py-0.5 rounded-full">
                          <CircleAlert className="size-4" />
                        </span>
                      )}
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-2">
                        {doc.subType === 'CrewID' ? (
                          <CreditCard className="size-4 text-content-brand-bold" />
                        ) : (
                          <IdCard className="size-4 text-content-brand-bold" />
                        )}
                        <span className="text-sm font-medium text-content-primary-bold">{doc.subType}</span>
                      </div>
                      {expired ? (
                        <span className="text-xs font-medium text-content-negative-bold bg-background-negative-subtle px-2 py-0.5 rounded-full tracking-wide">
                          Expired {dayjs(doc.expiryDate).format('MMM D, YYYY')}
                        </span>
                      ) : (
                        <span className="text-xs font-medium text-background-notice-bold bg-background-notice-subtle px-2 py-0.5 rounded-full tracking-wide">
                          Expires {dayjs(doc.expiryDate).format('MMM D, YYYY')}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}

              {/* Compliance Documents */}
              {allComplianceDocs.map((doc: any) => {
                const expired = isExpired(doc.expiryDate);

                return (
                  <button
                    key={doc.id}
                    onClick={() => {
                      setSelectedComplianceDocId(doc.id);
                      setIsComplianceDocOpen(true);
                    }}
                    className="w-full text-left border border-border-L2 rounded-2xl px-4 py-2 flex flex-col gap-1 hover:bg-surface-L2 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-base font-semibold text-content-primary-bold">{doc.title}</p>
                      {expired ? (
                        <span className="flex items-center gap-1 text-xs font-medium text-content-negative-bold bg-background-negative-subtle px-2 py-0.5 rounded-full">
                          <TriangleAlert className="size-4" />
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs font-medium text-background-notice-bold bg-background-notice-subtle px-2 py-0.5 rounded-full">
                          <CircleAlert className="size-4" />
                        </span>
                      )}
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-2">
                        <IdCard className="size-4 text-content-brand-bold" />
                        <span className="text-sm font-medium text-content-primary-bold">{doc.subType}</span>
                      </div>
                      {expired ? (
                        <span className="text-xs font-medium text-content-negative-bold bg-background-negative-subtle px-2 py-0.5 rounded-full tracking-wide">
                          Expired {dayjs(doc.expiryDate).format('MMM D, YYYY')}
                        </span>
                      ) : (
                        <span className="text-xs font-medium text-background-notice-bold bg-background-notice-subtle px-2 py-0.5 rounded-full tracking-wide">
                          Expires {dayjs(doc.expiryDate).format('MMM D, YYYY')}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}

              {allCrewDocs.length === 0 && allComplianceDocs.length === 0 && (
                <EmptyState
                  icon={IdCard}
                  title="No documents yet"
                  description="Upload crew documents, manuals, or trip files to review them here."
                />
              )}
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <ViewDocumentDrawer
        isOpen={isCrewDocOpen}
        onClose={() => setIsCrewDocOpen(false)}
        documentId={selectedCrewDocId}
      />

      <ViewDocumentDrawer
        isOpen={isComplianceDocOpen}
        onClose={() => setIsComplianceDocOpen(false)}
        documentId={selectedComplianceDocId}
      />
    </>
  );
}
